import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { AuditLogDetailsModal } from './AuditLogDetailsModal';
import { AuditLog } from '@/types/audit';
import AdminAvatarImage from './AdminAvatarImage';

interface AuditLogWithProfile extends AuditLog {
  username?: string;
  avatar_url?: string;
}

interface AuditLogsTableProps {
  logs: AuditLogWithProfile[];
  isLoading: boolean;
  page: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const eventTypeColors: Record<string, string> = {
  login: 'bg-green-500/10 text-green-500 border-green-500/20',
  logout: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  location_update: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  location_skip: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  profile_update: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  password_change: 'bg-red-500/10 text-red-500 border-red-500/20',
  email_change: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  match_created: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
  message_sent: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
  connection_created: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
  connection_deleted: 'bg-red-500/10 text-red-500 border-red-500/20',
  plan_change: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  photo_upload: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  photo_delete: 'bg-red-500/10 text-red-500 border-red-500/20',
  verification_submitted: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  verification_approved: 'bg-green-500/10 text-green-500 border-green-500/20',
  verification_rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
};

const eventTypeLabels: Record<string, string> = {
  login: 'Login',
  logout: 'Logout',
  location_update: 'Atualização de Localização',
  location_skip: 'Pular Localização',
  profile_update: 'Atualização de Perfil',
  password_change: 'Mudança de Senha',
  email_change: 'Mudança de Email',
  match_created: 'Match Criado',
  message_sent: 'Mensagem Enviada',
  connection_created: 'Conexão Criada',
  connection_deleted: 'Conexão Deletada',
  plan_change: 'Mudança de Plano',
  photo_upload: 'Upload de Foto',
  photo_delete: 'Foto Deletada',
  verification_submitted: 'Verificação Enviada',
  verification_approved: 'Verificação Aprovada',
  verification_rejected: 'Verificação Rejeitada',
};

export const AuditLogsTable = ({ logs, isLoading, page, totalCount, pageSize, onPageChange }: AuditLogsTableProps) => {
  const [selectedLog, setSelectedLog] = useState<AuditLogWithProfile | null>(null);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Helper functions to eliminate "Desconhecido"
  const formatIP = (ip?: string) => {
    return ip || 'Não registrado';
  };

  const formatLocation = (locationData?: AuditLog['location_data']) => {
    if (!locationData) return 'Não registrada';
    
    const parts = [];
    if (locationData.city) parts.push(locationData.city);
    if (locationData.state_code) parts.push(locationData.state_code);
    if (locationData.country) parts.push(locationData.country);
    
    return parts.length > 0 ? parts.join(', ') : 'Não registrada';
  };

  const formatDevice = (userAgent?: string) => {
    if (!userAgent) return 'Não identificado';
    if (userAgent.includes('Mobile') || userAgent.includes('Android') || userAgent.includes('iPhone')) return '📱 Mobile';
    if (userAgent.includes('Windows')) return '💻 Windows';
    if (userAgent.includes('Mac')) return '🖥️ Mac';
    if (userAgent.includes('Linux')) return '🐧 Linux';
    return '🌐 Navegador';
  };

  const formatAbsoluteDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <>
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Logs de Auditoria</CardTitle>
          <CardDescription className="text-white/60">
            Visualize todos os eventos de auditoria do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8 text-white/60">
                Carregando logs...
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-white/60">
                Nenhum log encontrado
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="grid grid-cols-[auto_1fr] gap-3 sm:gap-4 p-3 sm:p-4 border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      {/* Column 1: Avatar */}
                      <AdminAvatarImage
                        src={log.avatar_url}
                        fallback={log.username?.substring(0, 2).toUpperCase() || 'U'}
                        className="h-12 w-12 sm:h-14 sm:w-14 shrink-0"
                        fallbackClassName="bg-white/10 text-white"
                      />

                      {/* Column 2: User Info (vertical stack) */}
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <p className="font-medium truncate text-white text-sm sm:text-base">
                          {log.username || 'Usuário'}
                        </p>
                        <p className="text-xs sm:text-sm text-white/70 truncate">
                          {formatAbsoluteDateTime(log.created_at)}
                        </p>
                        {log.client_ip && (
                          <p className="text-xs text-white/60 truncate">
                            IP: {formatIP(log.client_ip)}
                          </p>
                        )}
                        {log.location_data && (log.location_data.city || log.location_data.state_code || log.location_data.country) && (
                          <p className="text-xs text-white/60 truncate">
                            📍 {formatLocation(log.location_data)}
                          </p>
                        )}
                        {log.user_agent && (
                          <p className="text-xs text-white/60 truncate">
                            {formatDevice(log.user_agent)}
                          </p>
                        )}
                        
                        {/* Action + Details Row */}
                        <div className="grid grid-cols-[1fr_auto] gap-2 items-center mt-1 pt-1 border-t border-white/5">
                          <Badge 
                            variant="outline" 
                            className={`${eventTypeColors[log.event_type] || ''} whitespace-nowrap text-xs w-fit`}
                          >
                            {eventTypeLabels[log.event_type] || log.event_type}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                            className="text-white hover:text-white hover:bg-white/10 h-8 px-2 sm:px-3"
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Detalhes</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="text-sm text-white/60">
                    Mostrando {((page - 1) * pageSize) + 1} a {Math.min(page * pageSize, totalCount)} de {totalCount} logs
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(page + 1)}
                      disabled={page >= totalPages}
                    >
                      Próxima
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedLog && (
        <AuditLogDetailsModal
          log={selectedLog}
          open={!!selectedLog}
          onOpenChange={(open) => !open && setSelectedLog(null)}
        />
      )}
    </>
  );
};
